import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useLocation } from 'react-router-dom';

const pageTitles: Record<string, string> = {
  '/': 'Dashboard',
  '/fizbo': 'FİZBO Takip',
  '/fizbo-kanban': 'FİZBO Kanban',
  '/portfoy': 'Portföy Yönetimi',
  '/talep-bul': 'Talep Bul',
  '/musteri-rehber': 'Müşteri & Rehber',
  '/toplu-mesaj': 'Toplu Mesaj Paneli',
  '/hatirlatmalar': 'Hatırlatıcılar',
  '/raporlar': 'Raporlar & Analiz',
  '/ayarlar': 'Ayarlar',
};

export default function Topbar() {
  const { isDark, toggleTheme } = useTheme();
  const { epUser, logout } = useAuth();
  const location = useLocation();
  const title = pageTitles[location.pathname] || 'Panel';
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleLogout = async () => {
    setShowProfileMenu(false);
    await logout();
  };

  return (
    <header
      className={`fixed top-0 right-0 left-0 lg:left-64 h-16 z-30 flex items-center justify-between px-4 md:px-6 transition-all duration-300 ${
        isDark ? 'bg-dark-card/80 backdrop-blur-glass border-b border-dark-border' : 'bg-white/80 backdrop-blur-glass border-b border-light-border'
      }`}
    >
      <h1 className={`text-lg md:text-xl font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
        {title}
      </h1>

      <div className="flex items-center gap-2 md:gap-3">
        {/* Search */}
        <div className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-search-line text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
          </div>
          <input
            type="text"
            placeholder="Ara..."
            className={`bg-transparent text-sm outline-none w-40 ${isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'}`}
          />
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
            isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={isDark ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
          </div>
        </button>

        {/* Notifications */}
        <button
          className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
            isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-notification-3-line text-lg" />
          </div>
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        </button>

        {/* Profile */}
        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className={`flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg transition-colors ${
              isDark ? 'hover:bg-dark-border' : 'hover:bg-light-border'
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-medium">
              {epUser?.full_name?.charAt(0)?.toUpperCase() || epUser?.email?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="hidden md:block text-left">
              <p className={`text-sm font-medium leading-tight ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                {epUser?.full_name || 'Kullanıcı'}
              </p>
              <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                {epUser?.role || 'Danışman'}
              </p>
            </div>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-arrow-down-s-line text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
            </div>
          </button>

          {showProfileMenu && (
            <>
              <div className="fixed inset-0 z-0" onClick={() => setShowProfileMenu(false)} />
              <div
                className={`absolute right-0 top-full mt-2 w-56 rounded-xl border overflow-hidden shadow-lg z-10 ${
                  isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'
                }`}
              >
                <div className={`p-3 border-b ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {epUser?.full_name || 'Kullanıcı'}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    {epUser?.email}
                  </p>
                </div>
                <button
                  onClick={() => { setShowProfileMenu(false); }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                    isDark
                      ? 'text-dark-text hover:bg-dark-border'
                      : 'text-light-text hover:bg-light-bg'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-settings-3-line" />
                  </div>
                  Ayarlar
                </button>
                <button
                  onClick={handleLogout}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-logout-box-r-line" />
                  </div>
                  Çıkış Yap
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}